<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useTheme } from 'vuetify'
import {
  type MessageReader,
  getAvatarColor,
  getInitials,
  normalizeAvatarUrl,
} from '../../chatModules'
import AuthenticatedVImg from '../AuthenticatedVImg.vue'
import { debugConsoleError, debugConsoleWarn } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { getMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { getMatrixMessageReaders } from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'
import { openChatForMentionedUser } from '@/modules/chat-matrix-fast/composables/useMentionNavigation'

interface Props {
  matrixRoomId: string
  matrixEventId?: string
  isMine: boolean
  /** Синхрон с зелёной галочкой на сообщении — иначе список залипает после первого пустого load */
  isSeen?: boolean
  readers?: MessageReader[]
}

const props = defineProps<Props>()

const { global: theme } = useTheme()

const isDark = computed(() => theme.name.value === 'dark')

const readers = ref<MessageReader[]>(props.readers ?? [])
const isLoading = ref(false)
const error = ref<string | null>(null)
const showReadersMenu = ref(false)
const menuPosition = ref<{ top: number; left: number } | null>(null)
const menuTriggerRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)

const lastLoadedEventId = ref<string | null>(null)
const MENU_MAX_HEIGHT = 400
const VIEWPORT_MARGIN = 8

const isRead = computed(() => readers.value.length > 0)

const getUserFullName = (reader: MessageReader): string => {
  const user = reader.user

  const parts = [
    user.first_name,
    user.middle_name,
    user.last_name,
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(' ') : 'Пользователь'
}

const getUserAvatar = (reader: MessageReader): string | undefined => {
  return normalizeAvatarUrl(reader.avatar || reader.user.avatar) || undefined
}

const statusText = computed(() => {
  if (isLoading.value) {
    return 'Загрузка...'
  }

  if (error.value) {
    return 'Ошибка загрузки'
  }

  if (readers.value.length === 0) {
    return null
  }

  const names = readers.value.map(getUserFullName)

  if (names.length <= 3) {
    return `Просмотрено: ${names.join(', ')}`
  }

  const firstReader = readers.value[0]
  const firstName = getUserFullName(firstReader)
  const remainingCount = readers.value.length - 1

  return `Просмотрено: ${firstName} и ещё ${remainingCount}`
})

const loadReaders = async () => {
  if (props.readers !== undefined) {
    readers.value = props.readers
    lastLoadedEventId.value = props.matrixEventId ?? null
    return
  }

  if (!props.matrixRoomId || !props.matrixEventId) {
    readers.value = []
    return
  }

  if (isLoading.value || lastLoadedEventId.value === props.matrixEventId) {
    return
  }

  isLoading.value = true
  error.value = null

  try {
    const matrixClient = getMatrixClient()
    const room = matrixClient.getRoom(props.matrixRoomId)

    if (!room) {
      readers.value = []
      return
    }

    readers.value = await getMatrixMessageReaders(matrixClient, room, props.matrixEventId)
    lastLoadedEventId.value = props.matrixEventId

    if (showReadersMenu.value && menuTriggerRef.value) {
      positionMenu(menuTriggerRef.value)
    }
  } catch (err) {
    debugConsoleError('[MessageReaders] Ошибка загрузки читателей:', err)
    error.value = 'Не удалось загрузить читателей'
  } finally {
    isLoading.value = false
  }
}

/** Позиционирует меню в viewport: предпочитает сверху, иначе снизу, с clamp и scroll. */
const positionMenu = (target: HTMLElement) => {
  nextTick(() => {
    const menuEl = menuRef.value
    const targetRect = target.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    if (!menuEl) {
      menuPosition.value = {
        left: targetRect.left + targetRect.width / 2 - 125,
        top: Math.max(VIEWPORT_MARGIN, targetRect.top - 172),
      }
      return
    }

    const spaceAbove = targetRect.top - VIEWPORT_MARGIN
    const spaceBelow = vh - targetRect.bottom - VIEWPORT_MARGIN
    const openAbove = spaceAbove >= Math.min(spaceBelow, MENU_MAX_HEIGHT)
    const available = Math.max(openAbove ? spaceAbove : spaceBelow, 120)

    menuEl.style.maxHeight = `${Math.min(MENU_MAX_HEIGHT, available)}px`

    const menuWidth = menuEl.offsetWidth || 250
    const menuHeight = menuEl.offsetHeight || 200

    let left = targetRect.left + targetRect.width / 2 - menuWidth / 2
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN
    } else if (left + menuWidth > vw - VIEWPORT_MARGIN) {
      left = vw - menuWidth - VIEWPORT_MARGIN
    }

    let top = openAbove
      ? targetRect.top - menuHeight + 28
      : targetRect.bottom + 2

    if (top < VIEWPORT_MARGIN) {
      top = VIEWPORT_MARGIN
    }
    if (top + menuHeight > vh - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, vh - menuHeight - VIEWPORT_MARGIN)
    }

    menuPosition.value = { left, top }
  })
}

const openReadersMenu = (event: MouseEvent) => {
  event.stopPropagation()

  const target = event.currentTarget as HTMLElement
  if (!target) {
    return
  }

  if (showReadersMenu.value) {
    closeReadersMenu()
    return
  }

  const targetRect = target.getBoundingClientRect()
  // Сначала монтируем меню, чтобы измерить реальную высоту
  menuPosition.value = {
    left: targetRect.left + targetRect.width / 2 - 125,
    top: Math.max(VIEWPORT_MARGIN, targetRect.top - 172),
  }
  showReadersMenu.value = true
  positionMenu(target)

  if (readers.value.length === 0 && !isLoading.value) {
    void loadReaders()
  }
}

const closeReadersMenu = () => {
  showReadersMenu.value = false
  menuPosition.value = null
}

const handleUserClick = async (reader: MessageReader) => {
  const mxid = String(reader.user?.id || reader.id || '').trim()
  if (!mxid.startsWith('@')) {
    return
  }

  closeReadersMenu()
  await openChatForMentionedUser(mxid)
}

const onDocumentClick = (e: MouseEvent) => {
  const target = e.target as Node
  const menu = menuRef.value
  const trigger = menuTriggerRef.value

  if (menu && !menu.contains(target) && trigger && !trigger.contains(target)) {
    closeReadersMenu()
  }
}

const handleReceiptsUpdated = (event: Event) => {
  const customEvent = event as CustomEvent<{ roomId?: string }>
  const roomId = customEvent.detail?.roomId

  if (roomId && roomId === props.matrixRoomId) {
    lastLoadedEventId.value = null
    void loadReaders()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  window.addEventListener('matrix-read-receipts-updated', handleReceiptsUpdated)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick)
  window.removeEventListener('matrix-read-receipts-updated', handleReceiptsUpdated)
})

watch(
  () => [props.matrixRoomId, props.matrixEventId, props.readers],
  () => {
    if (!props.matrixRoomId || !props.matrixEventId) {
      readers.value = []
      lastLoadedEventId.value = null
      return
    }

    if (props.readers !== undefined) {
      readers.value = props.readers
      lastLoadedEventId.value = props.matrixEventId ?? null
      return
    }

    lastLoadedEventId.value = null
    void loadReaders()
  },
  { immediate: true },
)

watch(
  () => props.isSeen,
  isSeen => {
    if (props.readers !== undefined || !props.matrixRoomId || !props.matrixEventId) {
      return
    }

    if (isSeen) {
      if (readers.value.length === 0) {
        lastLoadedEventId.value = null
        void loadReaders()
      }
      return
    }

    readers.value = []
    lastLoadedEventId.value = null
  },
)
</script>

<template>
  <div
    v-if="statusText"
    class="message-readers-status"
  >
    <VIcon
      icon="ri-check-double-line"
      size="14"
      class="message-readers-icon"
      :class="[
        isRead ? 'text-primary' : 'text-disabled',
      ]"
    />
    <span
      ref="menuTriggerRef"
      class="message-readers-text"
      :class="{ 'message-readers-text--clickable': readers.length > 0 || isLoading }"
      @click="openReadersMenu"
    >
      {{ statusText }}
    </span>
  </div>

  <Teleport to="body">
    <Transition name="readers-menu-fade">
      <div
        v-if="showReadersMenu && menuPosition"
        class="message-readers-menu-anchor"
        :class="{ 'v-theme--dark': isDark }"
        :style="{
          position: 'fixed',
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          zIndex: 9999,
        }"
      >
        <div
          ref="menuRef"
          class="message-readers-menu"
        >
          <div class="message-readers-menu-header">
            Просмотрено
          </div>
          <div class="message-readers-menu-list">
            <div
              v-for="reader in readers"
              :key="reader.id"
              class="message-readers-menu-item"
              @click="handleUserClick(reader)"
            >
              <VAvatar
                size="32"
                :class="[!getUserAvatar(reader) ? getAvatarColor(getUserFullName(reader)) : '']"
              >
                <AuthenticatedVImg
                  v-if="getUserAvatar(reader)"
                  :src="getUserAvatar(reader)"
                  :alt="getUserFullName(reader)"
                />
                <span
                  v-else
                  class="text-sm"
                >{{ getInitials(getUserFullName(reader)) }}</span>
              </VAvatar>
              <span class="message-readers-menu-item-name">
                {{ getUserFullName(reader) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.message-readers-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 8px 0;
  padding: 8px 16px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  width: 100%;
  text-align: center;
}

.message-readers-icon {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  flex-shrink: 0;
}

.message-readers-text {
  &--clickable {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;

    &:hover {
      color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    }
  }
}

.message-readers-menu-anchor {
  pointer-events: none;
}

.message-readers-menu {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  padding: 8px;
  min-width: 200px;
  max-width: 300px;
  max-height: min(400px, calc(100vh - 16px));
  overflow: hidden;
}

.message-readers-menu-header {
  flex-shrink: 0;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  margin-bottom: 4px;
}

.message-readers-menu-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  overflow-y: auto;
}

.message-readers-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
  cursor: pointer;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.08);
  }
}

.message-readers-menu-item-name {
  font-size: 14px;
  color: rgb(var(--v-theme-on-surface));
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.readers-menu-fade-enter-active,
.readers-menu-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.readers-menu-fade-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}

.readers-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.v-theme--dark {
  .message-readers-menu {
    background: rgb(var(--v-theme-surface));
    border-color: rgba(var(--v-theme-on-surface), 0.12);
    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.4);
  }

  .message-readers-menu-header {
    color: rgb(var(--v-theme-on-surface));
    border-bottom-color: rgba(var(--v-theme-on-surface), 0.12);
  }

  .message-readers-menu-item {
    &:hover {
      background-color: rgba(var(--v-theme-on-surface), 0.08);
    }
  }

  .message-readers-menu-item-name {
    color: rgb(var(--v-theme-on-surface));
  }
}
</style>
